import {
  SimpleAnswerEnum,
  DiscountTypeEnum,
  FundLevelsEnum,
  CurrencyEnum,
  EntityTypeEnum,
} from '../../enums/entity.enum';
import {
  RecyclingTimeLimitEnum,
  RecyclingProceedsLimitEnum,
  LpClawbackCalculatedOnEnum,
  LpClawbackTimeLimitFromEnum,
  MgmtFeeFromEnum,
  MgmtFeeToEnum,
  MgmtFeeCalcOnEnum,
  RateIndexEnum,
  CloseDateTypeEnum,
  ExtensionTypeEnum,
  LeverageBasisEnum,
  LeverageTypeEnum,
  LeverageUseEnum,
} from '../../enums/closedEnd.enum';
import { HurdleIndexEnum, LockUpEnum } from '../../enums/openEnd.enum';
import {
  IContact,
  IContractualExtension,
  IDiscount,
  IGate,
  IIncFeeRate,
  IMasterFund,
  IMgmtFeeRate,
  ISidePocket,
} from '../../interfaces/entities/entities.interface';
import {
  StoreModel,
  StoreModelType,
  StoreModelDate,
} from '@aksia/infrastructure';
import { AssetModeEnum } from '../../enums/secondary.enum';
import { CoInvStatusEnum } from '../../enums/coInvestment.enum';
import { TagCategoryEnum } from '../../enums/tagCategory.enum';
import { ITagDTO } from '../../interfaces/taxonomy/taxonomy.interfaces';
import { WritableSignal } from '@angular/core';

//#region Common

export class MasterFund extends StoreModel implements IMasterFund {
  id: number | undefined = undefined;
  fundId: number | undefined = undefined;
  name: string | undefined = undefined;
}

export class QuantumTag extends StoreModel implements ITagDTO {
  tagId: number | undefined = undefined;
  tagCategory: TagCategoryEnum | undefined = undefined;
}

export class LegalRegion extends StoreModel {
  override $toDTO = () => {
    return {};
  };
}

export class Location extends StoreModel {
  addressId: number | undefined = undefined;
  entityId: number | undefined = undefined;
  entityTypeId: EntityTypeEnum | undefined = undefined;
  address1: string | undefined = undefined;
  address2: string | undefined = undefined;
  city: string | undefined = undefined;
  state: string | undefined = undefined;
  zip: string | undefined = undefined;
  country: string | undefined = undefined;
  fax: string | undefined = undefined;
  phone: string | undefined = undefined;
  isPrimary: boolean | undefined = undefined;
  latitude: number | undefined = undefined;
  longitude: number | undefined = undefined;
}

export class Contact extends StoreModel implements IContact {
  id: number | undefined = undefined;
  contactId: number | undefined = undefined;
  group: number | undefined = undefined;
  firstName: string | undefined = undefined;
  lastName: string | undefined = undefined;
  email: string | undefined = undefined;
  phone: string | undefined = undefined;
  complianceOnly: boolean | undefined = undefined;
  isDeleted: boolean | undefined = undefined;
  isNew: boolean | undefined = undefined;
}

export class Discount extends StoreModel {
  id: number | undefined = undefined;
  discountType: DiscountTypeEnum | undefined = undefined;
  discountRate: number | undefined = undefined;
  discountDesc: string | undefined = undefined;
  markedForDeletion: boolean | undefined = undefined;
}

export class ContractualExtension extends StoreModel {
  id: number | undefined = undefined;
  duration: number | undefined = undefined;
  type: ExtensionTypeEnum | undefined = undefined;
}

//#endregion

//#region Open

export class Lockup extends StoreModel {
  id: number | undefined = undefined;
  lockupDurationMinRange: number | undefined = undefined;
  lockupDurationMaxRange: number | undefined = undefined;
  lockupType: LockUpEnum | undefined = undefined;
  lockupRolling: boolean | undefined = undefined;
  lockupFee: number | undefined = undefined;
  markedForDeletion: boolean | undefined = undefined;
}

export class Gate extends StoreModel implements IGate {
  id: number | undefined = undefined;
  gateLevel: FundLevelsEnum | undefined = undefined;
  gateMasterFundPercentage: number | undefined = undefined;
  gateFundPercentage: number | undefined = undefined;
  gateInvestorPercentage: number | undefined = undefined;
  gatePercentage: number | undefined = undefined;
  markedForDeletion: boolean | undefined = undefined;
}

export class SidePocket extends StoreModel implements ISidePocket {
  id: number | undefined = undefined;
  sidePocketLevel: FundLevelsEnum | undefined = undefined;
  sidePocketLimit: number | undefined = undefined;
  markedForDeletion: boolean | undefined = undefined;
}

export class MgmtFeeRate extends StoreModel implements IMgmtFeeRate {
  id: number | undefined = undefined;
  mgmtFeeRatePercentage: number | undefined = undefined;
  mgmtFeeRateAmountFrom: number | undefined = undefined;
  mgmtFeeRateAmountTo: number | undefined = undefined;
  markedForDeletion: boolean | undefined = undefined;
}

export class IncFeeRate extends StoreModel implements IIncFeeRate {
  id: number | undefined = undefined;
  incFeeRatePercentage: number | undefined = undefined;
  incFeeMinRatePercentage: number | undefined = undefined;
  incFeeMaxRatePercentage: number | undefined = undefined;
  hasHurdle: SimpleAnswerEnum | undefined = undefined;
  hurdleRateType: RateIndexEnum | undefined = undefined;
  hurdleIndex: HurdleIndexEnum | undefined = undefined;
  hurdleIndexDesc: string | undefined = undefined;
  hurdleRate: number | undefined = undefined;
  hurdleMinRatePercentage: number | undefined = undefined;
  hurdleMaxRatePercentage: number | undefined = undefined;
  markedForDeletion: boolean | undefined = undefined;
}

export class OpenMgmtFeeDiscount extends StoreModel implements IDiscount {
  id: number | undefined = undefined;
  discountType: DiscountTypeEnum | undefined = undefined;
  discountRate: number | undefined = undefined;
  discountDesc: string | undefined = undefined;
  markedForDeletion: boolean | undefined = undefined;
}

export class IncentiveFeeDiscount extends StoreModel implements IDiscount {
  id: number | undefined = undefined;
  discountType: DiscountTypeEnum | undefined = undefined;
  discountRate: number | undefined = undefined;
  discountDesc: string | undefined = undefined;
  markedForDeletion: boolean | undefined = undefined;
}

//#endregion

//#region Closed

export class ContractualFinalCloseExtension
  extends StoreModel
  implements IContractualExtension
{
  id: number | undefined = undefined;
  duration: number | undefined = undefined;
  type: ExtensionTypeEnum | undefined = undefined;
}

export class ContractualInvestmentPeriodExtension
  extends StoreModel
  implements IContractualExtension
{
  id: number | undefined = undefined;
  duration: number | undefined = undefined;
  type: ExtensionTypeEnum | undefined = undefined;
}

export class ContractualTermExtension
  extends StoreModel
  implements IContractualExtension
{
  id: number | undefined = undefined;
  duration: number | undefined = undefined;
  type: ExtensionTypeEnum | undefined = undefined;
}

export class ClosedEndInterimDate extends StoreModel {
  id: number | undefined = undefined;

  @StoreModelType(() => StoreModelDate)
  date: Date | undefined = undefined;

  type: CloseDateTypeEnum | undefined = undefined;
  description: string | undefined = undefined;
}

export class Leverage extends StoreModel {
  id: number | undefined = undefined;
  fundId: number | undefined = undefined;
  leverageBasis: LeverageBasisEnum | undefined = undefined;
  leverageType: LeverageTypeEnum | undefined = undefined;
  leveragePercentageMin: number | undefined = undefined;
  leveragePercentageMax: number | undefined = undefined;
  leveragePercentageTarget: number | undefined = undefined;
  leverageMultipleMin: number | undefined = undefined;
  leverageMultipleMax: number | undefined = undefined;
  leverageMultipleTarget: number | undefined = undefined;
  leverageDesc: string | undefined = undefined;
}

export class RecyclingTimeLimit extends StoreModel {
  id: number | undefined = undefined;
  timeLimitType: RecyclingTimeLimitEnum | undefined = undefined;
  monthsFromAcquisition: number | undefined = undefined;
}

export class RecyclingProceedsLimit extends StoreModel {
  id: number | undefined = undefined;
  proceedsType: RecyclingProceedsLimitEnum | undefined = undefined;
  proceedsDesc: string | undefined = undefined;
}

export class LpClawbackPerLimit extends StoreModel {
  id: number | undefined = undefined;
  lpClawbackPerLimitRate: number | undefined = undefined;
  lpClawbackCalculatedOnType: LpClawbackCalculatedOnEnum | undefined =
    undefined;
}

export class LpClawbackTimeLimit extends StoreModel {
  id: number | undefined = undefined;
  lpClawbackTimeLimitYears: number | undefined = undefined;
  lpClawbackTimeLimitFrom: LpClawbackTimeLimitFromEnum | undefined = undefined;
}

export class MgmtFeeAmount extends StoreModel {
  id: number | undefined = undefined;
  mgmtFeeRate: number | undefined = undefined;
  mgmtFeeCalcOnType: MgmtFeeCalcOnEnum | undefined = undefined;
  mgmtFeeCalcOnDesc: string | undefined = undefined;
  useOfLeverage: LeverageUseEnum | undefined;
  markedForDeletion: boolean | undefined = undefined;
}

export class MgmtFeePeriod extends StoreModel {
  id: number | undefined = undefined;
  mgmtFeeTimeFrom: MgmtFeeFromEnum | undefined = undefined;
  mgmtFeeTimeFromDesc: string | undefined = undefined;

  @StoreModelType(() => StoreModelDate)
  mgmtFeeTimeFromDate: Date | undefined = undefined;

  mgmtFeeTimeTo: MgmtFeeToEnum | undefined = undefined;
  mgmtFeeTimeToDesc: string | undefined = undefined;

  @StoreModelType(() => StoreModelDate)
  mgmtFeeTimeToDate: Date | undefined = undefined;

  @StoreModelType(() => MgmtFeeAmount)
  mgmtFeeAmounts: Array<MgmtFeeAmount> | undefined = undefined;
  markedForDeletion: boolean | undefined = undefined;
}

export class CarriedInterestTieredRate extends StoreModel {
  id: number | undefined = undefined;
  feeRate: number | undefined = undefined;
  prefReturn: number | undefined = undefined;
  multiple: number | undefined = undefined;
  hasCatchup: SimpleAnswerEnum | undefined = undefined;
  catchupRate: number | undefined = undefined;
  markedForDeletion: boolean | undefined = undefined;
}

export class ClosedMgmtFeeDiscount extends StoreModel {
  id: number | undefined = undefined;
  discountType: DiscountTypeEnum | undefined = undefined;
  discountRate: number | undefined = undefined;
  discountDesc: string | undefined = undefined;
  markedForDeletion: boolean | undefined = undefined;
}

export class CarriedInterestDiscount extends StoreModel {
  id: number | undefined = undefined;
  discountType: DiscountTypeEnum | undefined = undefined;
  discountRate: number | undefined = undefined;
  discountDesc: string | undefined = undefined;
  markedForDeletion: boolean | undefined = undefined;
}

//#endregion

//#region Secondary

export class SecondaryStoreModel extends StoreModel {
  $mode: AssetModeEnum | undefined = undefined;
}

export class SecondaryAsset extends SecondaryStoreModel {
  id: number | undefined = undefined;
  secondaryDataFieldsId: number | undefined = undefined;
  assetId: number | undefined = undefined;
  assetName: string | undefined = undefined;
  vintage: number | undefined = undefined;

  @StoreModelType(() => StoreModelDate)
  recordDate: Date | undefined = undefined;

  currency: CurrencyEnum | undefined = undefined;
  navRecordDate: number | undefined = undefined;
  uncalledCapital: number | undefined = undefined;
  cashFlowsSinceRefDate: number | undefined = undefined;
  unadjustedInvestmentAmount: number | undefined = undefined;
  adjustedInvestmentAmount: number | undefined = undefined;
  showInReport: boolean | undefined = undefined;
  sellersCommitment: number | undefined = undefined;
  aksiaProposedPrice: number | undefined = undefined;
  finalStatus: CoInvStatusEnum | undefined = undefined;
  independentSponsorSACV: SimpleAnswerEnum | undefined = undefined;

  override $onTransform = () => {
    this.$mode = this.assetId ? AssetModeEnum.Linking : AssetModeEnum.Editing;
  };
}

export class NoteStoreModel extends StoreModel {
  $cleanup: WritableSignal<boolean> | undefined = undefined;
}

export class Note extends NoteStoreModel {
  entityId: number | undefined = undefined;
  entityTypeId: EntityTypeEnum | undefined = undefined;
  $tag: string | undefined = undefined;
  fieldName: string | undefined = undefined;
  note: string | undefined = undefined;
  system: string | undefined = undefined;
  modifiedBy: string | undefined = undefined;
  modifiedOn: string | undefined = undefined;
}

//#endregion
